import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const DANGEROUS = new Set(["MAINTENANCE_MODE", "DISABLE_NEW_JOBS"]);

// Multi-line settings: render a textarea instead of a single-line input
// so newline-delimited values (URL lists, etc.) round-trip correctly.
const MULTILINE = new Set(["IG_VALIDATION_URLS"]);

// Human-readable descriptions for known settings — surfaced inline so
// admins don't have to grep the codebase to remember what each key does.
// Keys not listed fall back to a generic "no description" muted hint.
const SETTING_DESCRIPTIONS = {
  MAX_SOURCE_DURATION_FREE_SEC:
    "Max source video length for Free users (in seconds). Sources longer than this are rejected at /probe.",
  MAX_SOURCE_DURATION_PRO_SEC:
    "Max source video length for Pro users (in seconds). Bump this dynamically — workers pick it up within 30s (cache TTL).",
  TELEGRAM_SAFE_FILE_LIMIT_MB:
    "Files above this size fall back to S3-link delivery instead of in-chat send. Applies to Free users.",
  TELEGRAM_SAFE_FILE_LIMIT_PRO_MB:
    "Pro-only safe limit. On Local Bot API the practical max is ~2 GB. Above this size we still fall back to S3-link.",
  REFINE_BUFFER_SEC:
    "Smart Refine buffer in seconds (Sprint 4 Phase 1). Larger values widen the partial-download window so the user can refine further from the chosen cut without a re-download.",
  PRO_SUBSCRIPTION_STARS:
    "Monthly Telegram-Stars price for the Pro plan.",
  PRO_BG_FULLSOURCE_MODE:
    "Sprint 4 Phase 2 policy — when to auto-cache the full source for Pro users so refines are instant. eager = after every Pro cut (best UX, highest bandwidth). lazy = on first /refine of a source (cheaper bandwidth, first-refine still uses Phase 1 partial-redownload toast). off = kill-switch.",
  IG_VALIDATION_URLS:
    "Newline-separated list of Instagram URLs the cron job hits to monitor cookie longevity.",
};

// Settings whose values represent a duration in seconds. We show a
// humanized hint ("14400s · 4h 0m") next to the effective value.
const DURATION_SEC_KEYS = new Set([
  "MAX_SOURCE_DURATION_FREE_SEC",
  "MAX_SOURCE_DURATION_PRO_SEC",
  "REFINE_BUFFER_SEC",
]);

const DURATION_MIN_KEYS = new Set([]);

function humanizeSeconds(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function Settings() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [confirmKey, setConfirmKey] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setRows(data);
    setEdits({});
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (key, value) => {
    try {
      await api.put(`/admin/settings/${key}`, { value });
      toast.success(`${key} updated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSubmit = async (row) => {
    const newVal = edits[row.key];
    if (DANGEROUS.has(row.key) && newVal === "true") {
      setConfirmKey(row.key);
      return;
    }
    await save(row.key, newVal);
  };

  const onReset = async (row) => {
    await save(row.key, "");
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Settings</h1>
        <div className="text-xs font-mono text-muted-foreground">
          {rows.length} keys · runtime overrides win
        </div>
      </div>

      <div className="filmstrip-divider" />

      <div className="space-y-px bg-border">
        {rows.map((r) => {
          const draft = edits[r.key] ?? (r.runtime_override ?? "");
          const isDirty = draft !== (r.runtime_override ?? "");
          const dangerous = DANGEROUS.has(r.key);
          return (
            <div
              key={r.key}
              data-testid={`setting-${r.key}`}
              className="bg-background grid grid-cols-[1fr_320px] gap-6 p-4"
            >
              <div>
                <div className="font-mono text-sm flex items-center gap-2">
                  {r.key}
                  {dangerous && (
                    <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-red-400 border border-red-500/40 px-1">
                      danger
                    </span>
                  )}
                  {DURATION_SEC_KEYS.has(r.key) && r.effective_value && (
                    <span className="text-[10px] font-mono text-tg-accent/80 bg-tg-accent/10 border border-tg-accent/20 px-1.5 py-0.5 rounded-sm">
                      = {humanizeSeconds(r.effective_value) || "?"}
                    </span>
                  )}
                </div>
                {SETTING_DESCRIPTIONS[r.key] && (
                  <div className="text-xs text-muted-foreground mt-1 leading-snug max-w-md">
                    {SETTING_DESCRIPTIONS[r.key]}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  env: <code className="text-foreground/70">{r.env_value ?? "—"}</code>
                  {" · "}
                  effective:{" "}
                  <code className="text-foreground/70">
                    {r.effective_value ?? "—"}
                  </code>
                </div>
              </div>
              <div className="flex gap-1.5 items-start">
                {MULTILINE.has(r.key) ? (
                  <textarea
                    value={draft}
                    onChange={(e) =>
                      setEdits((s) => ({ ...s, [r.key]: e.target.value }))
                    }
                    placeholder="one value per line · empty = unset"
                    rows={5}
                    className="flex-1 bg-secondary/30 border border-border px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-foreground/40 resize-y min-h-[100px]"
                    data-testid={`setting-input-${r.key}`}
                  />
                ) : (
                  <input
                    value={draft}
                    onChange={(e) =>
                      setEdits((s) => ({ ...s, [r.key]: e.target.value }))
                    }
                    placeholder="runtime override (empty = unset)"
                    className="flex-1 bg-secondary/30 border border-border px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-foreground/40"
                    data-testid={`setting-input-${r.key}`}
                  />
                )}
                <button
                  onClick={() => onSubmit(r)}
                  disabled={!isDirty}
                  data-testid={`setting-save-${r.key}`}
                  className="px-2.5 py-1.5 text-xs border border-border disabled:opacity-30 hover:bg-secondary/40"
                >
                  Save
                </button>
                {r.runtime_override != null && (
                  <button
                    onClick={() => onReset(r)}
                    className="px-2.5 py-1.5 text-xs border border-border text-muted-foreground hover:text-foreground"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirmKey} onOpenChange={(o) => !o && setConfirmKey(null)}>
        <DialogContent className="bg-background border border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Confirm dangerous change</DialogTitle>
            <DialogDescription className="text-xs font-mono text-muted-foreground">
              This setting affects every user immediately on save.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm py-2">
            You are enabling <code className="font-mono text-red-400">{confirmKey}</code>.
            This impacts all users immediately. Continue?
          </div>
          <DialogFooter>
            <button
              onClick={() => setConfirmKey(null)}
              className="px-4 py-2 text-sm border border-border"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                const k = confirmKey;
                setConfirmKey(null);
                await save(k, "true");
              }}
              data-testid="dangerous-confirm-btn"
              className="px-4 py-2 text-sm bg-red-500 text-white"
            >
              Enable
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
